import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import { ReactNode, useMemo, useState } from "react";
import {
  AnalysisFilter,
  DurBucket,
  EventListCategory,
  ListQuery,
  getAnalysisEvents,
  getAnalysisOverview,
  getAnalysisRegions,
} from "../../api/analysis";
import { readShopZoneError } from "../../api/shopZone";
import { GlassIcon, RemoveButton } from "../../assets/icons/icons";
import { PageLayout } from "../../components/PageLayout";
import useGetUserGroupSub from "../../hooks/useGetUserGroupSub";
import { useI18n } from "../../locales";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";
import AnalysisMap, { MapMode, REGION_METRICS, RegionMetric } from "./AnalysisMap";
import { DurationChart, MonthlyChart, VehicleTypeSection, WeekHourChart } from "./AnalysisCharts";
import DetailModal, { DetailSpec, DetailTab } from "./DetailModal";
import EventRawModal from "./EventRawModal";
import {
  EVENT_GROUPS,
  EventTypeMeta,
  PERIOD_PRESETS,
  delta,
  fmt1,
  fmtInt,
  fmtMinutes,
  fmtPct,
  n,
  per10kHours,
} from "./analysisUtil";

/**
 * 빅데이터 분석 (docs/빅데이터-분석-페이지-기획.md 1차 범위)
 *
 *   A 요약 KPI · B1 사고·위험운전 핫스팟 · B2 행정동 단계구분도 · C 월·요일×시간대 추이 · D 차종별 사고율
 *
 * 범위(소속)는 서버가 로그인 계정으로 정한다. 화면의 소속 선택은 하위 소속으로 좁히는 데만 쓴다.
 * 2차(위치로그 격자 집계가 필요한 밀집도·기울기·음영지역)는 일 단위 집계 테이블이 생긴 뒤 붙인다.
 */

const { md } = theme.screen;
type Group = EventTypeMeta["group"];

const DEFAULT_PRESET = "1y";

const errText = (e: unknown) => (e ? readShopZoneError(e).message : null);

export default function BigData() {
  const { width } = useWindowSize();
  const isMobile = width <= md;
  const { t } = useI18n();

  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [range, setRange] = useState<[string, string]>(
    () => PERIOD_PRESETS.find((p) => p.key === DEFAULT_PRESET)!.range()
  );
  const [groupSub, setGroupSub] = useState("all");
  // 하위 그룹 선택은 시군구(5자리) 이상 계정만 — 다른 화면의 그룹 선택(SelectBoxGroupId)과 같은 기준
  const user = userStorage.get();
  const canPickGroup = !!user && user.area.length <= 5;
  const { data: groupSubData } = useGetUserGroupSub();
  const [vhclType, setVhclType] = useState<string>("");
  // 검색어: 입력 중인 값과 조회에 쓰는 값을 나눈다. Enter·돋보기를 눌러야 조회한다 (조회가 무거워서)
  const [kwInput, setKwInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [mode, setMode] = useState<MapMode>("hotspot");
  const [metric, setMetric] = useState<RegionMetric>("rate");
  const [groups, setGroups] = useState<Set<Group>>(() => new Set<Group>(["rollover", "danger", "area"]));
  // 항목을 눌렀을 때의 상세 목록, 이벤트 한 건의 원시 데이터
  const [detail, setDetail] = useState<DetailSpec | null>(null);
  const open = (title: string, tabs: DetailTab[], note?: string, color?: string) => setDetail({ title, tabs, note, color });
  const [rawId, setRawId] = useState<number | null>(null);

  const rangeOk = /^\d{4}-\d{2}-\d{2}$/.test(range[0]) && /^\d{4}-\d{2}-\d{2}$/.test(range[1]) && range[0] <= range[1];
  const filter: AnalysisFilter = useMemo(
    () => ({
      startDate: range[0],
      endDate: range[1],
      groupId: groupSub,
      vhclTypeCd: vhclType === "" ? null : Number(vhclType),
      keyword,
    }),
    [range, groupSub, vhclType, keyword]
  );

  const common = { enabled: rangeOk, staleTime: 10 * 60 * 1000, refetchOnWindowFocus: false, retry: false };
  const overview = useQuery({ queryKey: ["analysis", "overview", filter], queryFn: () => getAnalysisOverview(filter), ...common });
  const events = useQuery({
    queryKey: ["analysis", "events", filter],
    queryFn: () => getAnalysisEvents(filter),
    ...common,
    enabled: rangeOk && mode !== "region",
  });
  const regions = useQuery({
    queryKey: ["analysis", "regions", filter],
    queryFn: () => getAnalysisRegions(filter),
    ...common,
    enabled: rangeOk && mode === "region",
  });

  const d = overview.data;
  const k = d?.kpi;
  const p = d?.prevKpi;

  const typeNames = useMemo(() => new Map((d?.vhclTypes ?? []).map((t) => [String(t.code), t.name])), [d]);
  const typeName = (code: number | null) => (code === null || code === undefined ? "" : typeNames.get(String(code)) ?? "");

  const toggleGroup = (g: Group) =>
    setGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });

  const mapLoading =
    mode === "region"
      ? regions.isFetching
        ? "행정동 집계 중… (처음 조회는 몇 초 걸린다)"
        : errText(regions.error)
      : events.isFetching
      ? "이벤트 지점 불러오는 중…"
      : errText(events.error);

  return (
    // 데스크톱은 통계 화면처럼 바디 없이 그려 페이지 전체가 스크롤된다
    <PageLayout withoutBody={!isMobile} withBreadcrumb>
      <Page>
        <FilterPanel>
          <Head>
            <h1>빅데이터 분석</h1>
            <span className="sub">
              운행·사고 데이터를 기간·소속·차종으로 모아 본다
              {d && ` · ${d.period.start} ~ ${d.period.end} (비교: ${d.period.prevStart} ~ ${d.period.prevEnd})`}
              {keyword && <b className="kw"> · 검색: {keyword}</b>}
            </span>
          </Head>

          <Filters>
            <div className="seg" role="group" aria-label="기간">
              {PERIOD_PRESETS.map((pr) => (
                <button
                  key={pr.key}
                  type="button"
                  aria-pressed={preset === pr.key}
                  onClick={() => {
                    setPreset(pr.key);
                    setRange(pr.range());
                  }}
                >
                  {pr.label}
                </button>
              ))}
            </div>
            <label className="dates">
              <input
                type="date"
                value={range[0]}
                max={range[1]}
                onChange={(e) => {
                  setPreset("");
                  setRange([e.target.value, range[1]]);
                }}
              />
              <span>~</span>
              <input
                type="date"
                value={range[1]}
                min={range[0]}
                onChange={(e) => {
                  setPreset("");
                  setRange([range[0], e.target.value]);
                }}
              />
            </label>
            {canPickGroup && (
              <select className="type" value={groupSub} onChange={(e) => setGroupSub(e.target.value)} aria-label="그룹">
                <option value="all">전체 그룹</option>
                {(groupSubData ?? []).map((g) => (
                  <option key={g.group_id} value={g.group_id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}
            <select className="type" value={vhclType} onChange={(e) => setVhclType(e.target.value)} aria-label="차종">
              <option value="">전체 차종</option>
              {(d?.vhclTypes ?? []).map((t) => (
                <option key={String(t.code)} value={String(t.code)}>
                  {t.name}
                </option>
              ))}
            </select>
            <form
              className="search"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                setKeyword(kwInput.trim());
              }}
            >
              <button type="submit" className="glass" aria-label="검색" title="검색">
                <GlassIcon />
              </button>
              <input
                type="text"
                value={kwInput}
                onChange={(e) => setKwInput(e.target.value)}
                placeholder={t.control.searchDeviceOwner}
                aria-label={t.control.searchDeviceOwner}
                title="Enter 를 누르면 조회한다"
                maxLength={50}
              />
              {kwInput && (
                <button
                  type="button"
                  className="clear"
                  aria-label="검색어 지우기"
                  onClick={() => {
                    setKwInput("");
                    setKeyword("");
                  }}
                >
                  <RemoveButton />
                </button>
              )}
            </form>
            {!rangeOk && <span className="warn">기간을 확인한다</span>}
          </Filters>
        </FilterPanel>

        {overview.isLoading && <Notice>요약을 계산하는 중… 처음 조회는 몇 초 걸린다.</Notice>}
        {overview.error && <Notice className="bad">불러오지 못했다 — {errText(overview.error)}</Notice>}

        {k && p && d && (
          <Card>
            <CardHead>
              <h2>요약</h2>
              <span className="sub">전기 = 바로 앞 같은 길이의 기간 · 항목을 누르면 목록이 나온다</span>
            </CardHead>
            <Kpis>
              <Kpi
                title="운행 대수"
                onClick={() => open("운행 대수", [VEH("차량별 운행", "trips", true)])}
                value={`${fmtInt(k.vehicles)}대`}
                diff={delta(k.vehicles, p.vehicles)}
                sub={`등록 ${fmtInt(d.devices.registered)}대 · 최근 7일 수신 ${fmtPct(d.devices.active7d, d.devices.registered)}`}
              />
              <Kpi
                title="운행 건수"
                onClick={() => open("운행 건수", [{ kind: "trips", label: "운행 목록", query: {} }, VEH("차량별", "trips", true)])} value={`${fmtInt(k.trips)}건`} diff={delta(k.trips, p.trips)} />
              <Kpi
                title="운행시간"
                onClick={() =>
                  open("운행시간", [
                    VEH("차량별 운행시간", "hours", true),
                    { kind: "trips", label: "이상치 운행 (합계에서 뺌)", query: { durBucket: "abnormal" } },
                  ])
                }
                value={`${fmtInt(k.hours)}시간`}
                diff={delta(k.hours, p.hours)}
                sub={`이상치 제외 ${fmtInt(n(k.odd_trips) + n(k.open_trips))}건 (24시간 초과·시각 이상·종료 없음)`}
              />
              <Kpi
                title="이동거리"
                onClick={() => open("이동거리", [VEH("차량별 이동거리", "km", true)])} value={`${fmtInt(k.km)}km`} diff={delta(k.km, p.km)} />
              <Kpi
                title="차량전복 감지"
                onClick={() =>
                  open("차량전복 감지", [
                    EV("전체", "rollover"),
                    EV("119 출동", "119"),
                    EV("센터 입고", "center"),
                    EV("사고 미발생", "false"),
                    EV("미처리", "pending"),
                    VEH("차량별", "rollover"),
                  ])
                }
                value={`${fmtInt(k.rollover)}건`}
                diff={delta(k.rollover, p.rollover)}
                badWhenUp
                sub={`119 출동 ${fmtInt(k.rollover_119)} · 사고 미발생 ${fmtInt(k.rollover_false)} · 미처리 ${fmtInt(k.rollover_pending)}`}
              />
              <Kpi
                title="1만 운행시간당 전복"
                onClick={() =>
                  open(
                    "1만 운행시간당 전복",
                    [VEH("차량별 전복·운행시간", "rollover")],
                    "차량 하나의 사고율은 운행시간이 짧으면 크게 튄다. 운행시간과 함께 본다."
                  )
                }
                value={fmt1(per10kHours(k.rollover, k.hours))}
                diff={delta(per10kHours(k.rollover, k.hours), per10kHours(p.rollover, p.hours))}
                badWhenUp
                sub={`119 출동 기준 ${fmt1(per10kHours(k.rollover_119, k.hours))}`}
              />
              <Kpi
                title="위험운전"
                onClick={() => open("위험운전", [EV("위험운전", "danger"), EV("전복 전환", "to_rollover"), VEH("차량별", "danger")])}
                value={`${fmtInt(k.danger)}건`}
                diff={delta(k.danger, p.danger)}
                badWhenUp
                sub={`전복 전환 ${fmtInt(k.danger_to_rollover)}건 (${fmtPct(k.danger_to_rollover, k.danger)})`}
              />
              <Kpi
                title="전복 오탐률"
                onClick={() =>
                  open("전복 오탐", [EV("사고 미발생 확인", "false")], "오탐률 = 사고 미발생 확인 ÷ (차량전복 감지 − 미처리)")
                }
                value={fmtPct(k.rollover_false, n(k.rollover) - n(k.rollover_pending))}
                sub={`해제 사유 '사고 미발생' 기준 · 평균 처리 ${fmtMinutes(k.rollover_handle_min)}`}
              />
              <Kpi
                title="사용지역이탈·위험지역진입"
                onClick={() => open("사용지역이탈·위험지역진입", [EV("목록", "area"), VEH("차량별", "area")])}
                value={`${fmtInt(n(k.leave_area) + n(k.enter_danger))}건`}
                diff={delta(n(k.leave_area) + n(k.enter_danger), n(p.leave_area) + n(p.enter_danger))}
                badWhenUp
              />
              <Kpi
                title="활성 단말"
                onClick={() => open("활성 단말", [{ kind: "devices", label: "단말 수신" }])}
                value={fmtPct(d.devices.active7d, d.devices.registered)}
                sub={`최근 7일 ${fmtInt(d.devices.active7d)}대 · 30일 ${fmtInt(d.devices.active30d)}대 / 등록 ${fmtInt(d.devices.registered)}대`}
              />
            </Kpis>
          </Card>
        )}

        <Card>
          <CardHead>
            <h2>지도 분석</h2>
            <div className="seg">
              <button type="button" aria-pressed={mode === "hotspot"} onClick={() => setMode("hotspot")}>
                사고·위험운전 핫스팟
              </button>
              <button type="button" aria-pressed={mode === "density"} onClick={() => setMode("density")}>
                밀도
              </button>
              <button type="button" aria-pressed={mode === "region"} onClick={() => setMode("region")}>
                행정동 단계구분도
              </button>
            </div>
            {mode !== "region" ? (
              <div className="checks">
                {EVENT_GROUPS.map((g) => (
                  <label key={g.key}>
                    <input type="checkbox" checked={groups.has(g.key)} onChange={() => toggleGroup(g.key)} />
                    <i style={{ background: g.color }} />
                    {g.label}
                  </label>
                ))}
              </div>
            ) : (
              <select value={metric} onChange={(e) => setMetric(e.target.value as RegionMetric)} aria-label="지표">
                {REGION_METRICS.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            )}
          </CardHead>
          <AnalysisMap
            events={events.data ?? []}
            regions={regions.data}
            mode={mode}
            groups={groups}
            metric={metric}
            height={isMobile ? 420 : 600}
            loading={mapLoading}
            typeName={typeName}
            onOpenRaw={setRawId}
          />
          <p className="hint">
            {mode === "region"
              ? "운행은 시작 지점, 사고는 발생 지점이 속한 행정동으로 센다. 사고율은 운행 100시간 이상인 행정동만 색을 칠한다. 행정동을 누르면 상세가 나온다."
              : "마커를 누르면 유형·일시·처리 결과와 차량 정보가 나오고, 원시 데이터(그래프·표·엑셀)를 열 수 있다. 확대하면 묶음이 풀린다."}
          </p>
        </Card>

        {d && (
          <>
            <Grid2>
              <Card>
                <CardHead>
                  <h2>월별 운행·사고 추이</h2>
                  <span className="sub">점선은 전년 같은 달 운행 · 막대나 월을 누르면 목록</span>
                </CardHead>
                <MonthlyChart
                  data={d}
                  onPick={(ym) =>
                    open(`${ym} 운행·사고`, [
                      { kind: "trips", label: "운행", query: { month: ym } },
                      EV("전복·위험운전", "rollover_danger", { month: ym }),
                      VEH("차량별", "trips", false, { month: ym }),
                    ])
                  }
                />
              </Card>
              <Card>
                <CardHead>
                  <h2>요일 × 시간대</h2>
                  <span className="sub">칸을 누르면 목록</span>
                </CardHead>
                <WeekHourChart
                  data={d}
                  onPick={(kind, dw, weekday, hr) =>
                    open(
                      `${weekday}요일 ${hr}시 · ${WEEK_KIND_LABEL[kind]}`,
                      kind === "trips"
                        ? [{ kind: "trips", label: "운행", query: { dw, hr } }]
                        : [EV(WEEK_KIND_LABEL[kind], kind, { dw, hr })]
                    )
                  }
                />
              </Card>
            </Grid2>
            <Grid2>
              <Card>
                <CardHead>
                  <h2>운행시간 분포</h2>
                  <span className="sub">회색은 운행시간 합계에서 뺀 이상치 · 막대를 누르면 목록</span>
                </CardHead>
                <DurationChart
                  data={d}
                  onPick={(bucket, label) =>
                    open(`운행시간 ${label}`, [{ kind: "trips", label: "운행", query: { durBucket: bucket as DurBucket } }])
                  }
                />
              </Card>
              <Card>
                <CardHead>
                  <h2>차량전복 처리 결과</h2>
                </CardHead>
                <ResultBars
                  onPick={(i) => open(`차량전복 · ${i.label}`, [EV(i.label, i.category)], undefined, i.color)}
                  items={[
                    { category: "119", label: "119 출동", v: n(k?.rollover_119), color: "#d7263d" },
                    { category: "center", label: "센터 입고", v: n(k?.rollover_center), color: "#f28c28" },
                    { category: "false", label: "사고 미발생 확인", v: n(k?.rollover_false), color: "#9ca3af" },
                    {
                      category: "other",
                      label: "기타 사유",
                      v: Math.max(0, n(k?.rollover) - n(k?.rollover_119) - n(k?.rollover_center) - n(k?.rollover_false) - n(k?.rollover_pending)),
                      color: "#6b7280",
                    },
                    { category: "pending", label: "미처리", v: n(k?.rollover_pending), color: "#2563eb" },
                  ]}
                />
              </Card>
            </Grid2>
            <Card>
              <CardHead>
                <h2>차종별 운행·사고</h2>
                <span className="sub">줄이나 막대를 누르면 그 차종의 목록</span>
              </CardHead>
              <VehicleTypeSection
                data={d}
                onPick={(code, name) =>
                  open(`${name} 차종`, [
                    VEH("차량별", "trips", false, { vhclTypeCd: code }),
                    { kind: "trips", label: "운행", query: { vhclTypeCd: code } },
                    EV("전복·위험운전", "rollover_danger", { vhclTypeCd: code }),
                  ])
                }
              />
            </Card>
          </>
        )}
      </Page>

      <DetailModal filter={filter} spec={detail} typeName={typeName} onClose={() => setDetail(null)} onOpenRaw={setRawId} />
      <EventRawModal eventId={rawId} onClose={() => setRawId(null)} />
    </PageLayout>
  );
}

/** 상세 탭 만들기: 이벤트 목록 */
const EV = (label: string, category: EventListCategory, query: ListQuery = {}): DetailTab => ({
  kind: "events",
  label,
  query: { ...query, category },
});

/** 상세 탭 만들기: 차량별 집계 (sortBy 큰 값부터, onlyTrips 면 운행 있는 차량만) */
const VEH = (
  label: string,
  sortBy: "trips" | "hours" | "km" | "rollover" | "danger" | "area",
  onlyTrips = false,
  query: ListQuery = {}
): DetailTab => ({ kind: "vehicles", label, query, sortBy, onlyTrips });

const WEEK_KIND_LABEL = { trips: "운행 시작", rollover: "차량전복", danger: "위험운전" } as const;

function Kpi({
  title,
  value,
  sub,
  diff,
  badWhenUp,
  onClick,
}: {
  title: string;
  value: ReactNode;
  sub?: string;
  diff?: number | null;
  badWhenUp?: boolean;
  onClick?: () => void;
}) {
  const up = diff !== null && diff !== undefined && diff > 0;
  const tone = diff === null || diff === undefined || Math.abs(diff) < 0.05 ? "flat" : up === !!badWhenUp ? "bad" : "good";
  return (
    <KpiBox
      className={onClick ? "pick" : undefined}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={onClick ? "눌러서 목록 보기" : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="t">
        <span>{title}</span>
        {onClick && <span className="more">목록 ›</span>}
      </div>
      <div className="v">{value}</div>
      {diff !== undefined && (
        <div className={`d ${tone}`}>
          {diff === null ? "전기 자료 없음" : `전기 대비 ${up ? "▲" : diff < 0 ? "▼" : ""} ${Math.abs(diff).toFixed(1)}%`}
        </div>
      )}
      {sub && <div className="s">{sub}</div>}
    </KpiBox>
  );
}

type ResultItem = { category: EventListCategory; label: string; v: number; color: string };

function ResultBars({ items, onPick }: { items: ResultItem[]; onPick: (i: ResultItem) => void }) {
  const total = items.reduce((a, b) => a + b.v, 0);
  if (!total) return <Notice>기간 안에 차량전복 감지가 없다.</Notice>;
  return (
    <Results>
      <div className="stack">
        {items
          .filter((i) => i.v > 0)
          .map((i) => (
            <span
              key={i.label}
              style={{ width: `${(i.v / total) * 100}%`, background: i.color }}
              title={`${i.label} ${i.v}건 — 눌러서 목록 보기`}
              onClick={() => onPick(i)}
            />
          ))}
      </div>
      <table>
        <tbody>
          {items.map((i) => (
            <tr
              key={i.label}
              className={i.v > 0 ? "pick" : undefined}
              onClick={i.v > 0 ? () => onPick(i) : undefined}
              title={i.v > 0 ? "눌러서 목록 보기" : undefined}
            >
              <td>
                <i style={{ background: i.color }} />
                {i.label}
              </td>
              <td>{fmtInt(i.v)}건</td>
              <td>{fmtPct(i.v, total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="note">해제 사유 문구로 나눴다. 운영자가 직접 적은 사유는 '기타 사유'로 묶었다. 항목을 누르면 해당 건 목록이 나온다.</div>
    </Results>
  );
}

// ── 스타일 ───────────────────────────────────────────────────
// 통계 화면(stastics)과 같은 패널 모양: 연회색 바탕 + 그림자. 모바일은 흰 바탕에 그림자 없음
const panelCss = `
  background: ${theme.color.neutral_50};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  min-width: 0;
  @media (max-width: ${md}px) {
    background: ${theme.color.white};
    box-shadow: none;
    border-radius: 8px;
  }
`;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 23px;
  padding-bottom: 20px;
  @media (max-width: ${md}px) {
    gap: 12px;
    padding-bottom: 0;
  }
`;

const FilterPanel = styled.div`
  ${panelCss}
  border-radius: 10px;
  min-height: 92px;
  padding: 16px 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  @media (max-width: ${md}px) {
    min-height: 0;
    padding: 14px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
  }
  .sub {
    font-size: 13px;
    color: ${theme.color.neutral_500};
  }
  .sub .kw {
    color: ${theme.color.green_500};
    font-weight: 600;
  }
`;

const segCss = `
  display: inline-flex;
  flex-wrap: wrap;
  button {
    font-size: 13px;
    padding: 6px 12px;
    border: 1px solid ${theme.color.neutral_300};
    background: #fff;
    color: ${theme.color.neutral_700};
    cursor: pointer;
    margin-left: -1px;
  }
  button:first-of-type { border-radius: 6px 0 0 6px; margin-left: 0; }
  button:last-of-type { border-radius: 0 6px 6px 0; }
  button[aria-pressed="true"] {
    background: ${theme.color.green_500};
    border-color: ${theme.color.green_500};
    color: #fff;
    position: relative;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  .seg {
    ${segCss}
  }
  .dates {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }
  input[type="date"],
  select.type {
    height: 34px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 13px;
    color: ${theme.color.neutral_700};
    background: #fff;
  }
  select.type {
    min-width: 140px;
    max-width: 220px;
    cursor: pointer;
  }
  /* 다른 화면의 '기기 명, 소유자 명을 검색' 입력창과 같은 모양을 이 줄 높이(34px)에 맞춘다 */
  .search {
    position: relative;
    display: inline-flex;
    align-items: center;
    width: 260px;
  }
  .search input {
    width: 100%;
    height: 34px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 30px 0 36px;
    font-size: 13px;
    font-weight: 500;
    color: ${theme.color.neutral_700};
    background: #fff;
  }
  .search input:focus {
    outline: none;
    border-color: ${theme.color.green_500};
  }
  .search .glass,
  .search .clear {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .search .glass {
    left: 11px;
  }
  .search .clear {
    right: 8px;
  }
  @media (max-width: ${md}px) {
    select.type {
      flex: 1 1 140px;
      max-width: none;
    }
    .search {
      flex: 1 1 100%;
      width: auto;
    }
  }
  .warn {
    color: ${theme.color.red_500};
    font-size: 12px;
  }
`;

const Notice = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff;
  color: ${theme.color.neutral_500};
  font-size: 13px;
  &.bad {
    color: ${theme.color.red_600};
  }
`;

const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: ${md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const KpiBox = styled.div`
  background: #fefefe;
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 10px;
  padding: 14px 16px;
  min-width: 0;
  &.pick {
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  &.pick:hover,
  &.pick:focus-visible {
    outline: none;
    border-color: ${theme.color.green_500};
    box-shadow: 0 2px 8px rgba(37, 134, 63, 0.15);
  }
  .t {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    font-size: 12.5px;
    color: ${theme.color.neutral_500};
  }
  .t .more {
    flex: none;
    font-size: 11px;
    color: ${theme.color.neutral_400};
  }
  &.pick:hover .t .more {
    color: ${theme.color.green_500};
  }
  .v {
    font-size: 22px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }
  .d {
    font-size: 12px;
    margin-top: 2px;
  }
  .d.good {
    color: ${theme.color.green_500};
  }
  .d.bad {
    color: ${theme.color.red_500};
  }
  .d.flat {
    color: ${theme.color.neutral_400};
  }
  .s {
    font-size: 11.5px;
    color: ${theme.color.neutral_400};
    margin-top: 4px;
    line-height: 1.45;
    overflow-wrap: anywhere;
  }
`;

const Card = styled.section`
  ${panelCss}
  border-radius: 15px;
  padding: 20px 24px;
  .apexcharts-bar-area,
  .apexcharts-heatmap-rect,
  .apexcharts-xaxis-label {
    cursor: pointer;
  }
  @media (max-width: ${md}px) {
    padding: 14px;
  }
  .hint {
    margin: 8px 0 0;
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
`;

const CardHead = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: ${theme.color.neutral_800};
  }
  .sub {
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .seg {
    ${segCss}
  }
  .checks {
    display: inline-flex;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 13px;
  }
  .checks label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }
  .checks i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  select {
    height: 32px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 13px;
  }
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 23px;
  @media (max-width: ${md}px) {
    gap: 12px;
  }
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Results = styled.div`
  .stack {
    display: flex;
    height: 22px;
    border-radius: 6px;
    overflow: hidden;
    margin: 8px 0 14px;
  }
  .stack span {
    cursor: pointer;
  }
  .stack span:hover {
    filter: brightness(1.1);
  }
  tr.pick {
    cursor: pointer;
  }
  tr.pick:hover td {
    background: #f3f4f6;
  }
  tr.pick td:first-of-type {
    text-decoration: underline;
    text-decoration-color: #d1d5db;
    text-underline-offset: 3px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  td {
    padding: 6px 4px;
    border-bottom: 1px solid #f0f1f3;
    font-variant-numeric: tabular-nums;
  }
  td:not(:first-of-type) {
    text-align: right;
  }
  i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 6px;
  }
  .note {
    margin-top: 8px;
    font-size: 11.5px;
    color: ${theme.color.neutral_500};
  }
`;
